/**
 * Iter 239 (Lot 4) — Page admin Pré-prod Checklist (go-live).
 *
 * Tableau de bord visuel agrégeant tous les checks critiques avant
 * le déploiement production. Verdict global + détail par catégorie.
 *
 * Réservé aux admins/superadmins.
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  Rocket, CheckCircle2, AlertTriangle, XCircle, Plug, Loader2,
  RefreshCw, Trash2, ShieldCheck, Database, Zap, Settings as SettingsIcon,
  ArrowRight,
} from "lucide-react";

const VERDICT_STYLES = {
  ready: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", icon: CheckCircle2, label: "GO-LIVE PRÊT" },
  warning: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", icon: AlertTriangle, label: "AVERTISSEMENTS" },
  blocked: { bg: "bg-rose-500/10", border: "border-rose-500/30", text: "text-rose-400", icon: XCircle, label: "BLOCKERS À RÉSOUDRE" },
};

const STATUS_META = {
  ok: { color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", Icon: CheckCircle2, label: "OK" },
  warning: { color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", Icon: AlertTriangle, label: "Attention" },
  ko: { color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30", Icon: XCircle, label: "Erreur" },
  not_configured: { color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30", Icon: Plug, label: "Non configuré" },
};

const CATEGORY_ICONS = {
  "Intégrations": Zap,
  "Sécurité": ShieldCheck,
  "Configuration": SettingsIcon,
  "Données": Database,
  "Performance": Zap,
  "Plateforme": Rocket,
};

export default function PreflightCheckPage() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/deployment/preflight");
      setData(r.data);
    } catch (e) {
      console.error("Preflight load failed", e);
      if (e.response?.status === 403) {
        alert("Réservé aux administrateurs Blitzz");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const cleanupDemo = async () => {
    if (!window.confirm(
      "⚠️ ATTENTION : cette action supprime TOUTES les données démo (`is_demo=true`) " +
      "sur l'ensemble des tenants de la plateforme. Cette action est IRRÉVERSIBLE.\n\n" +
      "Confirmer ?"
    )) return;
    setCleaning(true);
    try {
      const r = await api.post("/deployment/cleanup-demo");
      alert(r.data.message);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  }
  if (!data) {
    return <div className="p-6 text-rose-400">Erreur de chargement</div>;
  }

  const verdict = VERDICT_STYLES[data.verdict] || VERDICT_STYLES.warning;
  const VerdictIcon = verdict.icon;

  // Group items by category
  const byCategory = (data.items || []).reduce((acc, it) => {
    const cat = it.category || "Autres";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(it);
    return acc;
  }, {});
  const categoryOrder = ["Sécurité", "Configuration", "Données", "Performance", "Intégrations", "Plateforme"];
  const categories = categoryOrder.filter(c => byCategory[c]).concat(
    Object.keys(byCategory).filter(c => !categoryOrder.includes(c))
  );

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="preflight-page">
      <PageHeader
        icon={<Rocket />}
        title="Pré-prod Checklist (Go-Live)"
        description="Vérifications complètes avant le déploiement production de la plateforme Blitzz."
        testId="preflight-header"
      />

      {/* Verdict global */}
      <div className={`bg-[#14101c] border ${verdict.border} rounded-xl p-6 mb-6 ${verdict.bg}`} data-testid="preflight-verdict">
        <div className="flex items-start gap-4">
          <VerdictIcon className={`w-10 h-10 ${verdict.text} flex-shrink-0`} />
          <div className="flex-1">
            <div className={`text-xs font-bold uppercase tracking-wider ${verdict.text} mb-1`}>{verdict.label}</div>
            <div className="text-2xl font-black text-white mb-2">{data.verdict_message}</div>
            <div className="text-xs text-slate-400">
              {data.summary.ok} OK · {data.summary.warning} avertissements · {data.summary.ko} erreurs · {data.summary.not_configured} non configurés sur {data.summary.total} contrôles
            </div>
          </div>
          <Button onClick={load} disabled={refreshing} size="sm" className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs" data-testid="refresh-btn">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Re-vérifier
          </Button>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-5">
        {categories.map(cat => {
          const items = byCategory[cat];
          const CatIcon = CATEGORY_ICONS[cat] || SettingsIcon;
          const okCount = items.filter(i => i.status === "ok").length;
          return (
            <div key={cat} className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid={`category-${cat}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CatIcon className="w-4 h-4 text-cyan-400" />
                  <h3 className="text-sm font-bold text-white">{cat}</h3>
                </div>
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">{okCount}/{items.length} OK</span>
              </div>
              <div className="space-y-2">
                {items.map(item => <PreflightItem key={item.id} item={item} />)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action danger : cleanup demo */}
      {user?.role === "superadmin" && (
        <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-5 mt-6" data-testid="superadmin-actions">
          <div className="flex items-start gap-3">
            <Trash2 className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-bold text-white mb-1">Nettoyer toutes les données démo (cross-tenant)</h3>
              <p className="text-xs text-slate-400 mb-3">
                Supprime toutes les entités avec <code className="text-rose-300">is_demo=true</code> sur l'ensemble des tenants.
                À exécuter juste avant le go-live, après que toutes les démos clients soient terminées.
              </p>
              <Button onClick={cleanupDemo} disabled={cleaning} className="bg-rose-500 text-white hover:bg-rose-600 font-bold text-xs" data-testid="cleanup-demo-btn">
                {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                Nettoyer toutes les données démo
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Liens utiles */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 mt-6" data-testid="useful-links">
        <h3 className="text-sm font-bold text-white mb-3">Liens utiles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <button onClick={() => navigate("/admin/integrations-health")} className="text-left bg-[#0b0811] border border-white/5 hover:border-white/15 rounded-lg p-3 text-xs flex items-center justify-between" data-testid="link-health">
            <div>
              <div className="font-bold text-white">Health Check API</div>
              <div className="text-slate-500 mt-0.5">Statut live des 13 intégrations</div>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
          </button>
          <button onClick={() => navigate("/admin/master")} className="text-left bg-[#0b0811] border border-white/5 hover:border-white/15 rounded-lg p-3 text-xs flex items-center justify-between" data-testid="link-master">
            <div>
              <div className="font-bold text-white">Master Admin</div>
              <div className="text-slate-500 mt-0.5">Tenants, plans, modules</div>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
          </button>
          <button onClick={() => navigate("/admin/aide")} className="text-left bg-[#0b0811] border border-white/5 hover:border-white/15 rounded-lg p-3 text-xs flex items-center justify-between" data-testid="link-help">
            <div>
              <div className="font-bold text-white">Centre d'aide</div>
              <div className="text-slate-500 mt-0.5">Notes de release, docs</div>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="text-xs text-slate-500 mt-6 text-center">
        Dernière vérification : {new Date(data.checked_at).toLocaleString("fr-FR")}
      </div>
    </div>
  );
}

function PreflightItem({ item }) {
  const meta = STATUS_META[item.status] || STATUS_META.not_configured;
  const StatusIcon = meta.Icon;
  return (
    <div className={`bg-[#0b0811] border rounded-lg p-3 ${meta.border} flex items-start gap-3`} data-testid={`item-${item.id}`}>
      <div className={`w-8 h-8 rounded-md ${meta.bg} flex items-center justify-center flex-shrink-0`}>
        <StatusIcon className={`w-4 h-4 ${meta.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="text-sm font-bold text-white">{item.label}</div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${meta.color} ${meta.bg} whitespace-nowrap`}>
            {meta.label}
          </span>
        </div>
        <div className="text-[11px] text-slate-400">{item.details}</div>
        {item.required_missing?.length > 0 && (
          <div className="text-[10px] text-rose-400 mt-1">→ Manquantes : {item.required_missing.join(", ")}</div>
        )}
      </div>
    </div>
  );
}
